import {
  listCodeSmsRecipients as sdkListRecipients,
  sendCodeBySms as sdkSendCodeBySms,
  type SendCodeSmsResponseDto,
  type SmsRecipientDto,
} from "~/lib/api";

/**
 * Network + mapping layer for the SMS-share flow (HAN-66), mirroring
 * `~/lib/codes.ts`. Keeps the generated SDK and the status-code → user-state
 * translation out of the start-handover screen. Each wrapper returns a
 * discriminated union so the caller stays in switch-form.
 */

export type SmsRecipientsResult =
  | { status: "ok"; data: SmsRecipientDto[] }
  | { status: "denied" }
  | { status: "not-found" }
  | { status: "error" };

/**
 * List the team members the outgoing doctor can text the code to
 * (`GET /sessions/:id/code/sms/recipients`). `denied` is the outgoing-doctor
 * gate (403); `not-found` collapses a missing session / inaccessible ward (404).
 */
export async function fetchSmsRecipients(sessionId: string): Promise<SmsRecipientsResult> {
  try {
    const result = await sdkListRecipients({ path: { id: sessionId } });
    if (result.data && !result.error) {
      return { status: "ok", data: result.data };
    }
    if (result.response.status === 403) return { status: "denied" };
    if (result.response.status === 404) return { status: "not-found" };
    return { status: "error" };
  } catch {
    return { status: "error" };
  }
}

export type SendCodeSmsResult =
  | { status: "ok"; data: SendCodeSmsResponseDto }
  | { status: "error"; message: string };

const GENERIC_SMS_ERROR = "Something went wrong sending the SMS. Read the code out instead.";

function messageForSmsStatus(status: number): string {
  switch (status) {
    case 400:
      return "This code is no longer current — regenerate it and try again.";
    case 403:
      return "Only the doctor who started this handover can send the code.";
    case 404:
      return "That team member is no longer on this ward.";
    case 409:
      return "This handover has no active code to send right now.";
    case 422:
      return "That team member has no phone number on file.";
    default:
      return GENERIC_SMS_ERROR;
  }
}

/**
 * Text the handover code to a chosen team member (`POST /sessions/:id/code/sms`).
 * The plaintext `code` (held in component state from generation) is re-sent so
 * the server can verify it before dispatch. On a 2xx the response carries
 * `sent` — `false` means the provider rejected the send (the code is still on
 * screen), which the caller surfaces as a soft failure rather than a hard error.
 */
export async function sendCodeBySms(
  sessionId: string,
  recipientUserId: string,
  code: string,
): Promise<SendCodeSmsResult> {
  try {
    const result = await sdkSendCodeBySms({
      path: { id: sessionId },
      body: { recipientUserId, code },
    });
    if (result.error || !result.data) {
      return { status: "error", message: messageForSmsStatus(result.response.status) };
    }
    return { status: "ok", data: result.data };
  } catch {
    return { status: "error", message: GENERIC_SMS_ERROR };
  }
}
